import { FC, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  Alert,
  Button,
  TextField,
  Stack,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useYupValidationResolver } from '@vs-online-only-app/ui';
import {
  RecoveryCodeFormFieldValues,
  validateResetTokenRequest,
} from '@vs-online-only-app/state';
import ResetPasswordBackButton from '../ResetPasswordBackButton';

const recoveryCodeValidationSchema = yup.object({
  code: yup.string().required('Required'),
});

type Props = {
  email: string;
  nextStep: (v: string) => void;
  previousStep: () => void;
  code?: string;
};

const ResetPasswordStep2: FC<Props> = ({
  email,
  code = '',
  nextStep,
  previousStep,
}) => {
  const resolver = useYupValidationResolver(recoveryCodeValidationSchema);
  const codeRef = useRef<HTMLInputElement>(null);
  const [recoveryCodeErrorMessage, setRecoveryCodeErrorMessage] = useState('');
  const [isSubmitButtonDisabled, setValidateButtonDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const recoveryCodeFormMethods = useForm<RecoveryCodeFormFieldValues>({
    defaultValues: {
      code,
    },
    resolver,
  });
  const { errors: recoveryCodeErrors } = recoveryCodeFormMethods.formState;

  const handleRecoveryCodeFieldBlur = async (
    fieldName: keyof RecoveryCodeFormFieldValues,
  ) => {
    recoveryCodeFormMethods.trigger(fieldName);
  };

  const handleRecoveryCodeFieldChange = useCallback(
    (params: { field: keyof RecoveryCodeFormFieldValues; value: string }) => {
      if (params.field === 'code') {
        setRecoveryCodeErrorMessage('');
        setValidateButtonDisabled(params.value.length === 0);
      }
      recoveryCodeFormMethods.setValue(params.field, params.value);
    },
    [recoveryCodeFormMethods],
  );

  const doValidateTokenRequest = useCallback(
    async (token: string) => {
      setIsLoading(true);
      try {
        await validateResetTokenRequest({
          email,
          token,
        });
        setIsLoading(false);
        nextStep(token);
      } catch (e) {
        console.error(e);
        setValidateButtonDisabled(true);
        setIsLoading(false);
        setRecoveryCodeErrorMessage('Incorrect recovery code');
      }
    },
    [email, nextStep],
  );

  const handleFormSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      recoveryCodeFormMethods.handleSubmit(async (formValues) => {
        const { code } = formValues;
        doValidateTokenRequest(code);
      })();
    },
    [doValidateTokenRequest, recoveryCodeFormMethods],
  );

  const triggerFormSubmit = useCallback(async () => {
    if (!codeRef.current || !codeRef.current.value) return;

    doValidateTokenRequest(codeRef.current.value);
  }, [doValidateTokenRequest, codeRef]);

  useEffect(() => {
    if (code) triggerFormSubmit();
    // We only want to trigger this effect once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack gap={3}>
      <ResetPasswordBackButton goToPreviousStep={previousStep} />
      <Typography variant="h4" align="center">
        Check your email
      </Typography>
      <form onSubmit={handleFormSubmit}>
        <Stack gap={3}>
          <Typography align="center">
            If you have an account with us,
            <br />
            a recovery code has been sent to your email
            <br />
            <strong>{email}</strong>
          </Typography>{' '}
          {recoveryCodeErrorMessage && (
            <Alert severity="error">{recoveryCodeErrorMessage}</Alert>
          )}
          <TextField
            {...recoveryCodeFormMethods.register('code')}
            inputRef={codeRef}
            label={`Enter recovery code here${
              recoveryCodeErrors?.code?.message
                ? ` - ${recoveryCodeErrors.code.message}`
                : ''
            }`}
            variant="outlined"
            fullWidth
            error={!!recoveryCodeErrors.code}
            onBlur={() => handleRecoveryCodeFieldBlur('code')}
            onChange={(e) =>
              handleRecoveryCodeFieldChange({
                field: 'code',
                value: e.target.value,
              })
            }
          />
        </Stack>
      </form>
      <Button
        disabled={isSubmitButtonDisabled || isLoading}
        variant="contained"
        onClick={triggerFormSubmit}
        size="large"
      >
        {isLoading ? (
          <CircularProgress color="inherit" size={20} />
        ) : (
          'Validate Code'
        )}
      </Button>
    </Stack>
  );
};

export default ResetPasswordStep2;
